'use strict';

import { listenDOMEvent } from '@jspartials/dom-api-utils';
import {
  addToCartItem,
  productCanBeAddedInCart,
  eventCartIsFull
} from '@jspartials/cart-utils';
import {
  setLoadingAddToCartButton,
  setSuccessAddToCart,
  setErrorAddToCart
} from '../addToCart';
import { notifyAfterAddedToCartSuccessfullyInProductDetails } from '@jscomponents/notification';
import { classes, addClassToElement } from '@jspartials/class-list-utils';
// Selectors
const selector = {
  button: {
    addToCart: '.js-button-add-to-cart'
  },
  input: {
    graduationLeftEye: '.js-input--graduation-left-eye',
    graduationRightEye: '.js-input--graduation-right-eye',
    isSubscription: '[name="isSubscription"]',
    legalConditionsWrapper: '.js-contact-lenses-legal-conditions-wrapper',
    legalConditions: '.js-input-legal-conditions'
  },
  plan: '[name="subscriptionPlan"]',
  graduations: {
    leftEye: '.js-graduations-left-eye',
    rightEye: '.js-graduations-right-eye'
  }
};

// Elements
const element = {
  button: {
    addToCart: document.querySelector(selector.button.addToCart) || null
  },
  input: {
    graduationLeftEye:
      document.querySelector(selector.input.graduationLeftEye) || null,
    graduationRightEye:
      document.querySelector(selector.input.graduationRightEye) || null,
    legalConditionsWrapper:
      document.querySelector(selector.input.legalConditionsWrapper) || null,
    legalConditions:
      document.querySelector(selector.input.legalConditions) || null
  },
  graduations: {
    leftEye:
      document
        .querySelector(selector.graduations.leftEye)
        .closest('.js-field--graduation') || null,
    rightEye:
      document
        .querySelector(selector.graduations.rightEye)
        .closest('.js-field--graduation') || null
  }
};

// Functions

/**
 * Check if the subscription is selected
 * @returns {Boolean}
 */
const subscriptionIsSelected = () => {
  const inputIsSubscriptionChecked =
    document.querySelector(`${selector.input.isSubscription}:checked`) || null;
  const isSubscription = inputIsSubscriptionChecked
    ? inputIsSubscriptionChecked.value
    : null;

  return isSubscription === 'yes';
};

/**
 * Get the selected plan element
 * @returns {HTMLElement|null}
 */
const selectedPlan = () => {
  return document.querySelector(`${selector.plan}:checked`) || null;
};

/**
 * Get the selected plan frequency
 * @returns {Number|null}
 */
const getFrequency = () => {
  const plan = selectedPlan();
  const isSubscription = subscriptionIsSelected();
  const frequency =
    plan && isSubscription ? parseInt(plan.dataset.frequency, 10) : null;

  return frequency;
};

/**
 * Get the selected plan quantity
 * @returns {Number|null}
 */
const getQuantity = () => {
  const plan = selectedPlan();
  const isSubscription = subscriptionIsSelected();
  const quantity =
    plan && isSubscription ? parseInt(plan.dataset.quantity, 10) : 2;

  return quantity;
};

/**
 * Get the selected plan quantity
 * @returns {Number|null}
 */
const getLensesPerUnit = () => {
  const lensesPerUnitWrapper =
    document.querySelector('[data-lenses-per-unit]') || null;
  const lensesPerUnit = lensesPerUnitWrapper
    ? parseInt(lensesPerUnitWrapper.dataset.lensesPerUnit, 10)
    : 0;
  return lensesPerUnit;
};

/**
 * Get options for add to cart
 * @param {String} eye - Left or right eye
 * @param {Number} graduation - Graduation value
 * @param {Number} frequency - Frequency value for subscription
 * @param {String} uniqueId - Unique id for subscription
 * @returns {String}
 */
const getOptionsForAddToCart = (
  eye,
  graduation,
  frequency,
  uniqueId,
  paymentBySubscription,
  lensesPerUnit
) => {
  return JSON.stringify([
    {
      optionId: 'hk_option_eye',
      selectedValueId: eye
    },
    {
      optionId: 'hk_option_eye_graduation',
      selectedValueId: graduation
    },
    {
      optionId: 'hk_option_frequency_subscription_plan',
      selectedValueId: frequency
    },
    {
      optionId: 'hk_option_subscription_unique_id',
      selectedValueId: uniqueId
    },
    {
      optionId: 'hk_option_payment_by_subscription',
      selectedValueId: paymentBySubscription
    },
    {
      optionId: 'hk_option_lenses_per_unit',
      selectedValueId: lensesPerUnit
    }
  ]);
};

/**
 * Get items for add to cart
 * @returns {Array}
 */
const getItemsForAddToCart = () => {
  const frequency = getFrequency();
  const lensesPerUnit = getLensesPerUnit();
  const graduationLeftEye = element.input.graduationLeftEye?.value || null;
  const graduationRightEye = element.input.graduationRightEye?.value || null;
  const uniqueId = generateUniqueId();
  const isSubscription = subscriptionIsSelected() ? 'yes' : 'no';
  const optionsLeftEye = getOptionsForAddToCart(
    'left',
    graduationLeftEye,
    frequency,
    uniqueId,
    isSubscription,
    lensesPerUnit
  );
  const optionsRightEye = getOptionsForAddToCart(
    'right',
    graduationRightEye,
    frequency,
    uniqueId,
    isSubscription,
    lensesPerUnit
  );
  const items = [];

  if (graduationLeftEye) {
    items.push(optionsLeftEye);
  }

  if (graduationRightEye) {
    items.push(optionsRightEye);
  }

  return items;
};

/**
 * Generate unique id
 * @returns {String}
 */
const generateUniqueId = () => {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
};

/**
 * Listen add to cart button for contact lenses
 * @returns {void}
 */
const listenAddToCartContactLenses = () => {
  listenDOMEvent(document, 'addToCartContactLenses', () => {
    const legalConditionsAccepted = element.input.legalConditions.checked;
    const graduationLeftEye = element.input.graduationLeftEye?.value || null;
    const graduationRightEye = element.input.graduationRightEye?.value || null;
    let countErrors = 0;

    if (!legalConditionsAccepted) {
      countErrors++;
      addClassToElement(
        element.input.legalConditionsWrapper,
        classes.has.error
      );

      element.input.legalConditionsWrapper.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }

    if (!graduationLeftEye) {
      countErrors++;
      console.log('graduationLeftEye', graduationLeftEye);
      console.log('element.graduation.leftEye', element.graduations.leftEye);
      addClassToElement(element.graduations.leftEye, classes.has.error);

      element.graduations.leftEye.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }

    if (!graduationRightEye) {
      countErrors++;
      addClassToElement(element.graduations.rightEye, classes.has.error);

      element.graduations.rightEye.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }

    if (countErrors > 0) {
      return;
    }

    const productId = element.button.addToCart.dataset.pid || null;
    const gtmListName = 'details-page';
    const totalQuantity = getQuantity();
    const quantityPerAddToCart = totalQuantity / 2;
    const items = getItemsForAddToCart();

    if (productId && items.length > 0) {
      setLoadingAddToCartButton(element.button.addToCart);
      productCanBeAddedInCart(productId, totalQuantity)
        .then((responseData) => {
          const { canBeAdded = false } = responseData;

          if (canBeAdded) {
            // Add to cart the first item
            addToCartItem(
              productId,
              quantityPerAddToCart,
              gtmListName,
              items[0]
            )
              .then(() => {
                if (items.length > 1) {
                  // Add to cart the second item
                  addToCartItem(
                    productId,
                    quantityPerAddToCart,
                    gtmListName,
                    items[1]
                  )
                    .then(() => {
                      setSuccessAddToCart(element.button.addToCart);
                      notifyAfterAddedToCartSuccessfullyInProductDetails(
                        productId,
                        items,
                        totalQuantity
                      );
                    })
                    .catch((error) => {
                      setErrorAddToCart(element.button.addToCart);
                      console.error(error);
                    });
                } else {
                  setSuccessAddToCart(element.button.addToCart);
                }
              })
              .catch((error) => {
                setErrorAddToCart(element.button.addToCart);
                console.error(error);
              });
          } else {
            setErrorAddToCart(element.button.addToCart);
            document.dispatchEvent(eventCartIsFull);
          }
        })
        .catch((error) => {
          setErrorAddToCart(element.button.addToCart);
          console.error(error);
        });
    } else {
      console.warn('Product ID or items are not defined');
    }
  });
};

export { listenAddToCartContactLenses };
